export interface CookieOptions {
  expires?: Date | string;
  maxAge?: number;
  domain?: string;
  path?: string;
  secure?: boolean;
  httpOnly?: boolean;
  sameSite?: 'strict' | 'lax' | 'none';
}

export interface Cookie {
  name: string;
  value: string;
}

export class CookieHelper {
  private cookies: Map<string, string> = new Map();

  constructor(cookieString: string) {
    this.parseCookies(cookieString);
  }

  private parseCookies(cookieString: string): void {
    if (!cookieString) return;

    cookieString.split('; ').forEach(cookie => {
      const [name, ...rest] = cookie.split('=');
      if (name) {
        // Handle empty values by joining rest or using empty string
        this.cookies.set(name.trim(), rest.join('=').trim());
      }
    });
  }

  get(name: string): Cookie | null {
    const value = this.cookies.get(name);
    return value !== undefined ? { name, value } : null;
  }

  getAll(): Cookie[] {
    return Array.from(this.cookies.entries()).map(([name, value]) => ({
      name,
      value,
    }));
  }

  has(name: string): boolean {
    return this.cookies.has(name);
  }

  set(name: string, value: string, options: CookieOptions = {}): void {
    this.cookies.set(name, value);

    let cookieString = `${name}=${value}`;

    if (options.expires) {
      const expires =
        options.expires instanceof Date
          ? options.expires.toUTCString()
          : options.expires;
      cookieString += `; expires=${expires}`;
    }

    if (options.maxAge !== undefined) {
      cookieString += `; max-age=${options.maxAge}`;
    }

    if (options.domain) {
      cookieString += `; domain=${options.domain}`;
    }

    if (options.path) {
      cookieString += `; path=${options.path}`;
    }

    if (options.secure) {
      cookieString += '; secure';
    }

    if (options.httpOnly) {
      cookieString += '; httponly';
    }

    if (options.sameSite) {
      cookieString += `; samesite=${options.sameSite}`;
    }

    document.cookie = cookieString;
  }

  delete(name: string): void {
    this.cookies.delete(name);
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
  }

  clear(): void {
    this.cookies.forEach((_, name) => {
      this.delete(name);
    });
    this.cookies.clear();
  }

  toString(): string {
    return Array.from(this.cookies.entries())
      .map(([name, value]) => `${name}=${value}`)
      .join('; ');
  }
}

// Create a singleton instance for convenience
export const cookieHelper =
  typeof document !== 'undefined' ? new CookieHelper(document.cookie) : null;
